import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { apiService } from '../services/api';
import { getAddonsFlat } from '../utils/addons';
import { orderDisplayNumber } from '../utils/orderDisplay';
import NotificationBell from './NotificationBell';

// Константы статусов заказа
const ORDER_STATUSES = {
  pending: { label: 'Создан', color: '#ffa500', icon: '⏳' },
  awaiting_confirmation: { label: 'Требуется изменение', color: '#ff9800', icon: '✏️' },
  confirmed: { label: 'Подтверждён', color: '#9c27b0', icon: '✅' },
  paid: { label: 'Оплачен', color: '#1fb9c4', icon: '💳' },
  preparing: { label: 'Готовится', color: '#2196f3', icon: '☕' },
  ready: { label: 'Готов к выдаче', color: '#4caf50', icon: '🎉' },
  completed: { label: 'Завершен', color: '#9e9e9e', icon: '✅' },
  cancelled: { label: 'Отменен', color: '#f44336', icon: '❌' }
};

const OrderHistoryView = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { carts, addItem, setSelectedCafe, clearCart } = useCart();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all'); // all, active, completed
  const [coffeeShops, setCoffeeShops] = useState({});
  const [cancellingOrderId, setCancellingOrderId] = useState(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [orderToCancel, setOrderToCancel] = useState(null);
  const [repeatingOrderId, setRepeatingOrderId] = useState(null);

  // Очистка корзины при переходе на страницу истории заказов, если она была заполнена из режима редактирования
  useEffect(() => {
    // Если корзина заполнена, но мы не в режиме редактирования (нет editOrderId в URL) - проверяем резервную копию
    const editOrderId = searchParams.get('editOrderId');
    const hasItemsInCart = Object.values(carts).some(cart => cart.items && cart.items.length > 0);
    const cartBackup = sessionStorage.getItem('quickly_coffee_cart_backup');
    
    if (!editOrderId && hasItemsInCart) {
      // Проверяем, есть ли резервная копия - если есть, значит пользователь был в режиме редактирования
      if (cartBackup) {
        try {
          const backupData = JSON.parse(cartBackup);
          if (backupData && backupData.carts && Object.keys(backupData.carts).length > 0) {
            // Восстанавливаем резервную копию
            localStorage.setItem('quickly_coffee_cart', JSON.stringify(backupData));
            sessionStorage.removeItem('quickly_coffee_cart_backup');
            
            // Перезагружаем страницу для применения изменений
            window.location.reload();
            
            if (process.env.NODE_ENV === 'development') {
              console.log('Restored cart backup on OrderHistoryView mount (leaving edit mode)');
            }
            return;
          }
        } catch (error) {
          console.error('Error restoring cart backup on OrderHistoryView:', error);
          sessionStorage.removeItem('quickly_coffee_cart_backup');
        }
      }
      
      // Если резервной копии нет, но корзина заполнена - это обычная корзина пользователя, не очищаем её
      // Очищаем только если была резервная копия (что означает режим редактирования)
      if (process.env.NODE_ENV === 'development') {
        console.log('Cart has items but no backup found - keeping user cart');
      }
    }
  }, [carts, searchParams]); // Выполняем при монтировании и при изменении carts или searchParams

  const fetchOrders = useCallback(async () => {
    const ordersData = await apiService.getUserOrders();

    // Сортируем по дате создания (новые сверху)
    const sortedOrders = ordersData.sort((a, b) =>
      new Date(b.created_at) - new Date(a.created_at)
    );

    setOrders(sortedOrders);
  }, []);

  // Загрузка заказов при монтировании компонента
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        await fetchOrders();

        // Загружаем информацию о кофейнях один раз
        try {
          const { prefetchCoffeeShops } = await import('../utils/coffeeShopsCache');
          const shops = await prefetchCoffeeShops();
          const shopsMap = {};
          shops.forEach(shop => {
            shopsMap[shop.id] = shop;
          });
          setCoffeeShops(shopsMap);
        } catch (err) {
          console.error('Error loading coffee shops:', err);
        }
      } catch (err) {
        setError(err.message);
        console.error('Error loading orders:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [fetchOrders]);

  // Автоматическое обновление активных заказов каждые 30 секунд
  useEffect(() => {
    const interval = setInterval(() => {
      fetchOrders().catch(err => {
        console.error('Error updating orders:', err);
      });
    }, 30000); // Обновляем каждые 30 секунд

    return () => clearInterval(interval);
  }, [fetchOrders]);

  const handleOrderClick = (orderId) => {
    navigate(`/order/${orderId}`);
  };

  const handleBackToMap = () => {
    navigate('/');
  };

  const handleNewOrder = () => {
    navigate('/');
  };

  const handleCancelButtonClick = (order, event) => {
    event.stopPropagation();
    setOrderToCancel(order);
    setIsCancelModalOpen(true);
  };

  const handleCloseCancelModal = () => {
    if (cancellingOrderId) return;
    setIsCancelModalOpen(false);
    setOrderToCancel(null);
  };

  const handleCancelOrder = async () => {
    if (!orderToCancel) {
      return;
    }

    const orderId = orderToCancel.id;

    try {
      setCancellingOrderId(orderId);
      await apiService.cancelOrder(orderId);
      // Обновляем список заказов после успешной отмены
      await fetchOrders();
    } catch (err) {
      console.error('Error cancelling order:', err);
      alert(`Ошибка при отмене заказа: ${err.message}`);
    } finally {
      setCancellingOrderId(null);
      setIsCancelModalOpen(false);
      setOrderToCancel(null);
    }
  };

  // Повторить заказ: добавить в корзину только те товары (и допники), которые есть в текущем меню
  const handleRepeatOrder = async (order, event) => {
    event.stopPropagation();
    if (!order || !order.coffee_shop_id) return;
    const shop = coffeeShops[order.coffee_shop_id];
    if (!shop) {
      alert('Не удалось определить кофейню заказа.');
      return;
    }

    try {
      setRepeatingOrderId(order.id);
      const fullOrder = await apiService.getOrder(order.id);
      if (!fullOrder?.items?.length) {
        alert('В заказе нет товаров для повторения.');
        return;
      }

      const menuItems = await apiService.getMenuItems(fullOrder.coffee_shop_id);
      const cafeInfo = { id: shop.id, name: shop.name, ...shop };
      setSelectedCafe(cafeInfo);

      clearCart();

      let addedCount = 0;
      for (const orderItem of fullOrder.items) {
        const menuItem = menuItems.find(item => item.id === orderItem.menu_item_id);
        if (!menuItem) continue; // товара нет в меню — не добавляем

        let selectedSize = null;
        if (orderItem.size_id) {
          selectedSize = menuItem.sizes?.find(s => s.id === orderItem.size_id) || null;
        }

        const menuAddonsFlat = getAddonsFlat(menuItem.addons || []);
        const selectedAddons = [];
        if (orderItem.addons && Array.isArray(orderItem.addons) && orderItem.addons.length > 0) {
          orderItem.addons.forEach(addonObj => {
            const addonId = addonObj?.addon_id != null ? addonObj.addon_id : addonObj?.id;
            if (addonId == null) return;
            const addon = menuAddonsFlat.find(a => a.id === addonId);
            if (addon) selectedAddons.push(addon); // только допники из текущего меню
          });
        }
        if (selectedAddons.length === 0 && orderItem.addon_ids && Array.isArray(orderItem.addon_ids)) {
          orderItem.addon_ids.forEach(addonIdOrObj => {
            const addonId = typeof addonIdOrObj === 'object' && addonIdOrObj !== null
              ? addonIdOrObj.id || addonIdOrObj.addon_id
              : addonIdOrObj;
            if (addonId != null) {
              const addon = menuAddonsFlat.find(a => a.id === addonId);
              if (addon) selectedAddons.push(addon);
            }
          });
        }

        const basePrice = selectedSize ? Number(selectedSize.price) : Number(menuItem.price);
        const addonsPrice = selectedAddons.reduce((sum, a) => sum + Number(a.price || 0), 0);
        const totalPrice = basePrice + addonsPrice;
        const formattedAddons = selectedAddons.map(a => ({
          id: a.id,
          name: a.name || 'Допник',
          price: a.price || 0,
          volume_weight: a.volume_weight ?? null,
        }));

        const cartItem = {
          ...menuItem,
          quantity: orderItem.quantity,
          price: totalPrice,
          size: selectedSize?.size_name || null,
          size_id: selectedSize?.id || null,
          customizations: {
            sizeId: selectedSize?.id || null,
            sizeName: selectedSize?.size_name || null,
            sizeVolumeWeight: selectedSize?.volume_weight ?? null,
            addons: formattedAddons,
          },
          addon_ids: formattedAddons.map(a => a.id),
        };
        addItem(cafeInfo, cartItem);
        addedCount += orderItem.quantity;
      }

      if (addedCount === 0) {
        alert('Ни один товар из этого заказа не найден в текущем меню.');
        return;
      }
      navigate('/cart');
    } catch (err) {
      console.error('Error repeating order:', err);
      alert(`Ошибка при повторении заказа: ${err.message}`);
    } finally {
      setRepeatingOrderId(null);
    }
  };

  // Вспомогательная функция для правильного парсинга даты из API
  const parseDateFromAPI = (dateString) => {
    if (!dateString) return null;
    
    // Если строка уже содержит 'Z' или '+', значит часовой пояс указан
    if (dateString.includes('Z') || dateString.includes('+') || dateString.includes('-', 10)) {
      return new Date(dateString);
    }
    
    // Если часовой пояс не указан, предполагаем, что это UTC и добавляем 'Z'
    // Это важно, так как сервер хранит время в UTC
    const utcString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    return new Date(utcString);
  };

  // Форматирование даты (использует локальное время пользователя)
  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Парсим дату с учетом того, что она приходит в UTC из базы
    const date = parseDateFromAPI(dateString);
    if (!date || isNaN(date.getTime())) return '';
    
    const now = new Date();
    
    // Получаем локальный часовой пояс браузера
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    // Проверяем, сегодня ли это
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) {
      return date.toLocaleTimeString('ru-RU', { 
        hour: '2-digit', 
        minute: '2-digit',
        timeZone: timeZone
      });
    }
    
    // Проверяем, вчера ли это
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();
    if (isYesterday) {
      return `Вчера, ${date.toLocaleTimeString('ru-RU', { 
        hour: '2-digit', 
        minute: '2-digit',
        timeZone: timeZone
      })}`;
    }
    
    // Иначе полная дата
    return date.toLocaleString('ru-RU', { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: timeZone
    });
  };

  // Компактная дата для списка: сегодня — время, иначе ДД.ММ
  const formatCompactDate = (order) => {
    if (!order?.created_at) return '';
    const date = parseDateFromAPI(order.created_at);
    if (!date || isNaN(date.getTime())) return '';
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) {
      return date.toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone,
      });
    }
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      timeZone,
    });
  };

  const getOrderItemLabels = (items) => {
    const list = Array.isArray(items) ? items : [];
    const labels = list.slice(0, 3).map((item) => {
      const name = item.item_name || item.menu_item?.name || `Товар #${item.menu_item_id}`;
      return item.quantity > 1 ? `${name} ×${item.quantity}` : name;
    });
    return {
      labels,
      extraCount: Math.max(0, list.length - 3),
    };
  };

  // Группировка заказов по датам
  const groupOrdersByDate = (ordersList) => {
    const groups = {
      today: [],
      yesterday: [],
      thisWeek: [],
      thisMonth: [],
      older: []
    };

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    const monthAgo = new Date(today);
    monthAgo.setMonth(monthAgo.getMonth() - 1);

    ordersList.forEach(order => {
      const orderDate = new Date(order.created_at);
      const orderDay = new Date(orderDate.getFullYear(), orderDate.getMonth(), orderDate.getDate());

      if (orderDay.getTime() === today.getTime()) {
        groups.today.push(order);
      } else if (orderDay.getTime() === yesterday.getTime()) {
        groups.yesterday.push(order);
      } else if (orderDate >= weekAgo) {
        groups.thisWeek.push(order);
      } else if (orderDate >= monthAgo) {
        groups.thisMonth.push(order);
      } else {
        groups.older.push(order);
      }
    });

    return groups;
  };

  // Фильтрация заказов
  const getFilteredOrders = () => {
    if (activeFilter === 'active') {
      return orders.filter(order => 
        order.status !== 'completed' && order.status !== 'cancelled'
      );
    }
    if (activeFilter === 'completed') {
      return orders.filter(order => 
        order.status === 'completed' || order.status === 'cancelled'
      );
    }
    return orders;
  };

  const filteredOrders = getFilteredOrders();
  const groupedOrders = groupOrdersByDate(filteredOrders);

  // Состояние загрузки
  if (loading) {
    return (
      <div className="order-history-container">
        <div className="header">
          <div className="flex space-between align-center">
            <div>
              <h1>История заказов</h1>
              <p className="subtitle">Загрузка...</p>
            </div>
            <button 
              className="btn btn-secondary"
              onClick={handleBackToMap}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '12px',
                padding: '8px 12px',
                color: 'white',
                fontWeight: '600'
              }}
            >
              Карта
            </button>
          </div>
        </div>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '400px',
          color: 'white',
          fontSize: '18px'
        }}>
          <div>Загружаем историю заказов...</div>
        </div>
      </div>
    );
  }

  // Состояние ошибки
  if (error) {
    return (
      <div className="order-history-container">
        <div className="header">
          <div className="flex space-between align-center">
            <div>
              <h1>История заказов</h1>
              <p className="subtitle">Ошибка загрузки</p>
            </div>
            <button 
              className="btn btn-secondary"
              onClick={handleBackToMap}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '12px',
                padding: '8px 12px',
                color: 'white',
                fontWeight: '600'
              }}
            >
              Карта
            </button>
          </div>
        </div>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '400px',
          color: 'white',
          fontSize: '18px',
          textAlign: 'center',
          padding: '20px'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}></div>
          <div style={{ marginBottom: '16px' }}>{error}</div>
          <button 
            className="btn"
            onClick={() => window.location.reload()}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '12px',
              padding: '12px 24px',
              color: 'white',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  // Рендер списка заказов в группе
  const renderOrderGroup = (groupOrders, groupTitle) => {
    if (groupOrders.length === 0) return null;

    return (
      <div className="order-group" key={groupTitle}>
        <h3 className="order-group-title">{groupTitle}</h3>
        {groupOrders.map((order) => {
          const status = ORDER_STATUSES[order.status] || ORDER_STATUSES.pending;
          const shop = coffeeShops[order.coffee_shop_id];
          const { labels, extraCount } = getOrderItemLabels(order.items);
          const canRepeat = order.status === 'completed' || order.status === 'cancelled';
          const canCancel = ['pending', 'awaiting_confirmation', 'confirmed'].includes(order.status);
          const price = order.total_amount != null ? Math.round(Number(order.total_amount)) : 0;

          return (
            <div
              key={order.id}
              className="order-card order-card--compact"
              onClick={() => handleOrderClick(order.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleOrderClick(order.id);
                }
              }}
            >
              <div className="order-card-meta">
                <div className="order-price">{price} ₽</div>
                <div className="order-time">{formatCompactDate(order)}</div>
              </div>

              <div className="order-card-main">
                <div className="order-cafe-line">
                  <span className="cafe-name">{shop?.name || 'Кофейня'}</span>
                  <span
                    className="order-status-dot"
                    style={{ backgroundColor: status.color }}
                    title={status.label}
                    aria-label={status.label}
                  />
                </div>
                <div className="order-items-line" title={labels.join(', ')}>
                  {labels.length > 0 ? (
                    <>
                      <span className="order-items-text">{labels.join(' · ')}</span>
                      {extraCount > 0 && (
                        <span className="order-items-more">+{extraCount}</span>
                      )}
                    </>
                  ) : (
                    <span className="order-items-text order-items-empty">Без позиций</span>
                  )}
                </div>
              </div>

              <div className="order-card-actions" onClick={(e) => e.stopPropagation()}>
                {canRepeat && (
                  <button
                    type="button"
                    className="order-repeat-btn"
                    onClick={(e) => handleRepeatOrder(order, e)}
                    disabled={repeatingOrderId === order.id}
                    title="Повторить заказ"
                    aria-label="Повторить заказ"
                  >
                    {repeatingOrderId === order.id ? (
                      <span className="order-repeat-spinner" aria-hidden />
                    ) : (
                      <span className="order-repeat-icon" aria-hidden>↻</span>
                    )}
                  </button>
                )}
                {canCancel && (
                  <button
                    type="button"
                    className="order-cancel-icon-btn"
                    onClick={(e) => handleCancelButtonClick(order, e)}
                    disabled={cancellingOrderId === order.id}
                    title="Отменить заказ"
                    aria-label="Отменить заказ"
                  >
                    {cancellingOrderId === order.id ? '…' : '✕'}
                  </button>
                )}
                {!canRepeat && !canCancel && (
                  <span className="order-card-chevron" aria-hidden>→</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="order-history-container">
      <div className="header">
        <div className="flex space-between align-center">
          <div>
            <h1>История заказов</h1>
            <p className="subtitle">Все ваши заказы</p>
          </div>
          <div className="header-right-actions">
            <NotificationBell />
            <button 
              className="btn btn-secondary"
              onClick={handleBackToMap}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '12px',
                padding: '8px 12px',
                color: 'white',
                fontWeight: '600'
              }}
            >
              Карта
            </button>
          </div>
        </div>

        {/* Фильтры */}
        <div className="order-filters">
          <button
            className={`filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
            onClick={() => setActiveFilter('all')}
          >
            Все ({orders.length})
          </button>
          <button
            className={`filter-btn ${activeFilter === 'active' ? 'active' : ''}`}
            onClick={() => setActiveFilter('active')}
          >
            Активные ({orders.filter(o => o.status !== 'completed' && o.status !== 'cancelled').length})
          </button>
          <button
            className={`filter-btn ${activeFilter === 'completed' ? 'active' : ''}`}
            onClick={() => setActiveFilter('completed')}
          >
            Завершенные ({orders.filter(o => o.status === 'completed' || o.status === 'cancelled').length})
          </button>
        </div>
      </div>

      <div className="order-history-content">
        {filteredOrders.length === 0 ? (
          <div className="empty-state">
            <div style={{ fontSize: '64px', marginBottom: '16px' }}></div>
            <h3>Заказов пока нет</h3>
            <p>Сделайте свой первый заказ в одной из наших кофеен</p>
            <button className="btn" onClick={handleNewOrder}>
              Выбрать кофейню
            </button>
          </div>
        ) : (
          <>
            {renderOrderGroup(groupedOrders.today, 'Сегодня')}
            {renderOrderGroup(groupedOrders.yesterday, 'Вчера')}
            {renderOrderGroup(groupedOrders.thisWeek, 'На этой неделе')}
            {renderOrderGroup(groupedOrders.thisMonth, 'В этом месяце')}
            {renderOrderGroup(groupedOrders.older, 'Ранее')}
          </>
        )}
      </div>

      {/* Плавающая кнопка "Новый заказ" */}
      {orders.length > 0 && (
        <button 
          className="floating-new-order-btn"
          onClick={handleNewOrder}
        >
          <span style={{ fontSize: '24px', marginRight: '8px' }}>+</span>
          Новый заказ
        </button>
      )}

      {isCancelModalOpen && orderToCancel && (
        <div className="modal-overlay" onClick={handleCloseCancelModal}>
          <div 
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '420px',
              padding: '28px',
              textAlign: 'center'
            }}
          >
            <h3 style={{ marginBottom: '16px', fontSize: '20px', color: 'white' }}>
              Отменить заказ #{orderDisplayNumber(orderToCancel)}?
            </h3>
            <p style={{ marginBottom: '20px', color: 'white', lineHeight: 1.5 }}>
              После отмены вы не сможете восстановить этот заказ. Если уже была произведена оплата, обратитесь в поддержку.
            </p>
            <div style={{
              background: '#f8f9fa',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px',
              textAlign: 'left'
            }}>
              <p style={{ margin: 0, fontSize: '15px', color: '#333' }}>
                <strong>Кофейня:</strong> {coffeeShops[orderToCancel.coffee_shop_id]?.name || '—'}
              </p>
              <p style={{ margin: '8px 0 0', fontSize: '14px', color: '#666' }}>
                <strong>Создан:</strong> {formatDate(orderToCancel.created_at)}
              </p>
              {(orderToCancel.discount_amount != null && Number(orderToCancel.discount_amount) > 0) && (
                <p style={{ margin: '8px 0 0', fontSize: '14px', color: '#2e7d32' }}>
                  <strong>Скидка (промокод):</strong> −{Number(orderToCancel.discount_amount).toFixed(2)} ₽
                </p>
              )}
              {(orderToCancel.loyalty_discount_amount != null && Number(orderToCancel.loyalty_discount_amount) > 0) && (
                <p style={{ margin: '8px 0 0', fontSize: '14px', color: '#2e7d32' }}>
                  <strong>Скидка (лояльность):</strong> −{Number(orderToCancel.loyalty_discount_amount).toFixed(2)} ₽
                </p>
              )}
              {(orderToCancel.loyalty_bonus_spent != null && Number(orderToCancel.loyalty_bonus_spent) > 0) && (
                <p style={{ margin: '8px 0 0', fontSize: '14px', color: '#2e7d32' }}>
                  <strong>Списание баллов:</strong> −{Number(orderToCancel.loyalty_bonus_spent).toFixed(2)} ₽
                </p>
              )}
              <p style={{ margin: '8px 0 0', fontSize: '14px', color: '#666' }}>
                <strong>Сумма:</strong> {orderToCancel.total_amount ? Number(orderToCancel.total_amount).toFixed(2) : 0} ₽
              </p>
            </div>
            <div className="modal-actions" style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
              
              <button 
                className="btn btn-cancel" 
                onClick={handleCancelOrder}
                disabled={cancellingOrderId === orderToCancel.id}
              >
                {cancellingOrderId === orderToCancel.id ? 'Отменяем…' : 'Отменить заказ'}
              </button>
              <button 
                className="btn btn-secondary" 
                onClick={handleCloseCancelModal}
                disabled={cancellingOrderId === orderToCancel.id}
              >
                Оставить заказ
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderHistoryView;

